import {
  LinkTag, MetaTag, ScriptTag, TitleTag
} from '@thd-nucleus/thd-helmet';

// Maps tag keys to their corresponding tag generator functions.
const tagGenerators = {
  title: (value) => new TitleTag(value),
  description: (value) => new MetaTag({ name: 'description', content: value, id: 'description' }),
  fbAdmins: (value) => new MetaTag({ property: 'fb:admins', content: value, id: 'fbAdmins' }),
  ogDescription: (value) => new MetaTag({ property: 'og:description', content: value, id: 'ogDescription' }),
  ogImage: (value) => new MetaTag({ property: 'og:image', content: value, id: 'ogImage' }),
  ogSiteName: (value) => new MetaTag({ property: 'og:site_name', content: value, id: 'ogSiteName' }),
  ogTitle: (value) => new MetaTag({ property: 'og:title', content: value, id: 'ogTitle' }),
  ogType: (value) => new MetaTag({ property: 'og:type', content: value, id: 'ogType' }),
  ogUrl: (value) => new MetaTag({ property: 'og:url', content: value, id: 'ogUrl' }),
  canonical: (value) => new LinkTag({ rel: 'canonical', href: value, id: 'canonical' }),
  alternate: (value) => {
    if (Array.isArray(value)) {
      return value
        .map((item) => {
          if (!item || typeof item !== 'object' || !item.href || !item.id) {
            return null;
          }
          const { href, hreflang, id } = item;

          const linkProps = { rel: 'alternate', href, id };
          if (hreflang) {
            linkProps.hreflang = hreflang;
          }
          return new LinkTag(linkProps);
        })
        .filter(Boolean);
    }

    if (typeof value === 'string') {
      return new LinkTag({ rel: 'alternate', href: value, id: 'alternate' });
    }

    if (typeof value === 'object' && value.href) {
      return new LinkTag({ rel: 'alternate', id: 'alternate', ...value });
    }

    return null;
  },
  googleSiteVerification: (value) => new MetaTag({
    name: 'google-site-verification',
    content: value,
    id: 'gscVerification'
  }),
  // This logic needs to be revisited to examine the default handling of falsy values and if we want a warning or not
  robots: (value) => {
    if (typeof value !== 'boolean') {
      // console.warn(
      //   `robots tag received invalid value: ${typeof value}. Expected boolean true or false.`
      // );
      return null;
    }

    return new MetaTag({
      name: value ? 'ROBOTS' : 'robots',
      content: value ? 'NOINDEX, NOFOLLOW' : 'max-image-preview:large',
      id: 'robots'
    });
  },
  // Generates an array of preload LinkTags for each image object in the array
  preloadImages: (images) => {
    return (Array.isArray(images) ? images : [])
      .map((img, idx) => {
        if (!img?.href) {
          return null;
        }
        const {
          href,
          rel = 'preload',
          id = img?.imgID ? img.imgID : `preloadImg${idx}`,
          as = 'image',
          media,
          imagesrcset,
          imagesizes,
          fetchpriority = 'high'
        } = img;

        return new LinkTag({
          rel,
          href,
          id,
          as,
          ...(media ? { media } : {}),
          ...(imagesrcset ? { imagesrcset } : {}),
          ...(imagesizes ? { imagesizes } : {}),
          fetchpriority
        });
      })
      .filter(Boolean);
  },
  // Generates an array of ScriptTag objects for each schema object in the array
  schemas: (schemas) => {
    return (Array.isArray(schemas) ? schemas : [])
      .map((schema) => {
        if (!schema?.content || !schema?.id) {
          return null;
        }
        return new ScriptTag({
          content: schema.content,
          id: schema.id
        });
      })
      .filter(Boolean);
  }
};

// Returns a tag object for a given tag name and value, or null if not supported or value is falsy.
const generateTag = (tagName, tagValue) => {
  // Disallow undefined, null, empty string, 0, NaN, etc., but keep boolean false valid
  if (!tagValue && tagValue !== false) {
    return null;
  }

  const generator = tagGenerators[tagName];

  if (!generator) {
    return null;
  }

  return generator(tagValue);
};

/**
 * Generates an array of tag objects (TitleTag, MetaTag, LinkTag, ScriptTag) for use in document head metadata.
 * Each key in the input object corresponds to a supported tag type and is mapped to its appropriate tag.
 *
 * Supported tag keys:
 * - title: {string} Page title
 * - description: {string} Meta description
 * - fbAdmins: {string} Facebook admin IDs
 * - googleSiteVerification: {string} Google site verification code
 * - ogDescription: {string} OpenGraph description
 * - ogImage: {string} OpenGraph image URL
 * - ogSiteName: {string} OpenGraph site name
 * - ogTitle: {string} OpenGraph title
 * - ogType: {string} OpenGraph type (e.g., 'website')
 * - ogUrl: {string} OpenGraph URL
 * - canonical: {string} Canonical URL
 * - alternate: {string|Array<{href: string, hreflang: string, id: string}>} Alternate language versions
 * - preloadImages: {Array<{href, rel?, id?, as?, media?, imagesrcset?, imagesizes?, fetchpriority?}>} Images to preload
 * - schemas: {Array<{content: string, id: string}>} JSON-LD schema scripts
 * - robots: {boolean} If true, sets NOINDEX, NOFOLLOW; if false, sets max-image-preview:large
 *
 * @param {Object} args - An object where keys are tag types (see above) and values are their contents.
 * @returns {Array} An array of tag objects for use with @thd-nucleus/thd-helmet.
 */
export const generateTags = (args) => Object.entries(args)
  .flatMap(([key, value]) => {
    const tag = generateTag(key, value);
    // Flatten arrays (like from preloadImages), otherwise return as array
    return Array.isArray(tag) ? tag : [tag];
  })
  .filter(Boolean);
